import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { crypto as cv } from '@photo-vault/shared';
import { useBlur } from '../../app/BlurContext.js';
import { cn } from '../../lib/cn.js';
import { Icon } from '../../ui/index.js';
import { StarRating } from '../rating/StarRating.js';
import { mediaKindOf } from './mediaType.js';
import { prefetchMedia, useMediaSource, type LoadingPhase } from './useMediaSource.js';

const VIEWER_PHASE_LABEL: Record<LoadingPhase, string> = {
  'awaiting-url': 'Richiesta link…',
  'cache-thumb': 'Cache anteprima…',
  'cache-cipher': 'Cache locale…',
  fetching: 'Scarico…',
  decrypting: 'Decifro…',
  thumbnailing: 'Anteprima…',
};

export interface MediaItemInfo {
  /** Photo.encryptedMeta ciphertext. Decrypted lazily by the Info panel so
   *  the parent doesn't pay the cost upfront for every row. */
  encryptedMeta: string;
  /** Photo.createdAt — when the row landed in DDB after upload. Plaintext. */
  uploadedAt: string;
  /** Photo.size — encrypted blob size on S3, in bytes. Plaintext. The
   *  decrypted meta also carries the original (pre-encryption) size; both
   *  are surfaced separately. */
  encryptedSize: number;
  /** K_album generation the blob is encrypted under. Absent → gen 1. */
  keyGen?: number;
}

export interface MediaItem {
  id: string;
  /** Presigned S3 URL. Null while the downloads/batch chunk for this id is
   *  still in flight — the grid renders the tile in `awaiting-url` state in
   *  that case. The fullscreen viewer never opens a tile without a url, so
   *  Stage / FilmstripThumb assume a string at the call site. */
  url: string | null;
  filename: string;
  /** Original mime type (from decryptedMeta). Drives image-only thumb
   *  generation in the worker; absent for legacy rows where meta wasn't
   *  decoded. */
  mimeType?: string;
  /** 1..5 stars decrypted from Photo.encryptedRating. Null/undefined → unrated. */
  rating?: number | null;
  /** Optional payload powering the fullscreen Info panel. When omitted the
   *  Info button is hidden — useful for call sites that don't have the row
   *  handy or don't want to expose metadata (none currently, but kept
   *  optional so a viewer-only flow can opt out cheaply). */
  info?: MediaItemInfo;
}

export interface MediaViewerProps {
  items: MediaItem[];
  initialIndex: number;
  albumKey: CryptoKey;
  onClose: () => void;
  /** Owner-mode: invoked when the user confirms delete on the current item.
   *  When omitted the delete affordance is hidden (viewer-mode safety net). */
  onDelete?: (id: string) => Promise<void> | void;
  /** Owner-mode: invoked when the user clicks a star on the current item.
   *  When omitted, stars render read-only (still visible if rating is set). */
  onRate?: (id: string, rating: number) => Promise<void> | void;
  /** Owner-mode: invoked when the user opens the tag picker for the current
   *  item. When omitted, the tag affordance is hidden (viewer-mode). */
  onEditTags?: (id: string) => void;
}

const MAX_SCALE = 6;
const MIN_SCALE = 1;
const DOUBLE_TAP_MS = 280;
const SWIPE_THRESHOLD_PX = 60;
const MOUSE_REVEAL_THRESHOLD_PX = 24;
const HOT_ZONE_TOP_PX = 100;
const HOT_ZONE_BOTTOM_PX = 140;
const HOT_ZONE_SIDE_PX = 80;
const EDGE_TAP_RATIO = 0.22;
const PRESENTATION_INTERVAL_MS = 6500;
const CROSSFADE_MS = 1400;
const KENBURNS_DURATION_MS = 8200;
const KENBURNS_VARIANTS = 4;

export function MediaViewer({
  items,
  initialIndex,
  albumKey,
  onClose,
  onDelete,
  onRate,
  onEditTags,
}: MediaViewerProps) {
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, initialIndex), Math.max(0, items.length - 1)),
  );
  const [chromeVisible, setChromeVisible] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const presentingRef = useRef(presenting);
  presentingRef.current = presenting;
  const chromeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouseAnchor = useRef<{ x: number; y: number } | null>(null);
  const scheduleHideChrome = useCallback(() => {
    if (chromeTimer.current) clearTimeout(chromeTimer.current);
    chromeTimer.current = setTimeout(() => setChromeVisible(false), 3500);
  }, []);
  const showChrome = useCallback(() => {
    setChromeVisible(true);
    scheduleHideChrome();
  }, [scheduleHideChrome]);
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const { clientX, clientY } = e;
      const inHotZone =
        clientY < HOT_ZONE_TOP_PX ||
        clientY > window.innerHeight - HOT_ZONE_BOTTOM_PX ||
        clientX < HOT_ZONE_SIDE_PX ||
        clientX > window.innerWidth - HOT_ZONE_SIDE_PX;
      if (inHotZone) {
        mouseAnchor.current = { x: clientX, y: clientY };
        showChrome();
        return;
      }
      const last = mouseAnchor.current;
      if (!last) {
        mouseAnchor.current = { x: clientX, y: clientY };
        return;
      }
      if (Math.hypot(clientX - last.x, clientY - last.y) > MOUSE_REVEAL_THRESHOLD_PX) {
        mouseAnchor.current = { x: clientX, y: clientY };
        showChrome();
      }
    },
    [showChrome],
  );

  useEffect(() => {
    showChrome();
    return () => {
      if (chromeTimer.current) clearTimeout(chromeTimer.current);
    };
  }, [showChrome]);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Hardware/browser back button → chiude il viewer invece di abbandonare
  // l'album. Piazziamo un marker in history al mount: il prossimo popstate
  // è il nostro e triggera onClose. Se il modal viene chiuso per altre vie
  // (X / ESC / swipe-down) il cleanup pop-pa il marker, così l'utente non
  // si ritrova un'entry fantasma da attraversare due volte. Usiamo un ref
  // su onClose per evitare di re-installare l'handler ad ogni render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    window.history.pushState({ photoVaultViewer: true }, '');
    const onPop = () => {
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (window.history.state?.photoVaultViewer) {
        window.history.back();
      }
    };
  }, []);

  // Keyboard navigation. ESC exits presentation first if active, otherwise
  // closes the viewer. F11 toggles presentation; we preventDefault so the
  // browser's native fullscreen toggle doesn't fight us — we drive the
  // Fullscreen API ourselves in the effect below.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault();
        setPresenting((p) => !p);
        return;
      }
      if (e.key === 'Escape') {
        if (presentingRef.current) setPresenting(false);
        else onClose();
        return;
      }
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items.length]);

  // Fullscreen API: tie window fullscreen to presentation mode. If the user
  // exits fullscreen externally (esc on some browsers, OS-level), keep state
  // in sync by leaving presentation mode too.
  useEffect(() => {
    if (!presenting) return;
    const root = document.documentElement;
    if (!document.fullscreenElement && root.requestFullscreen) {
      root.requestFullscreen().catch(() => {});
    }
    const onChange = () => {
      if (!document.fullscreenElement) setPresenting(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [presenting]);

  // Auto-advance during presentation. The interval depends on `index`, so any
  // manual navigation (arrow keys, edge tap, swipe, thumbnail click) restarts
  // the timer rather than firing back-to-back transitions.
  useEffect(() => {
    if (!presenting || items.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, PRESENTATION_INTERVAL_MS);
    return () => clearInterval(id);
  }, [presenting, items.length, index]);

  // Prefetch neighbors so swipes feel instant.
  useEffect(() => {
    const next = items[index + 1];
    const prev = items[index - 1];
    if (next?.url) prefetchMedia(next.id, next.url, albumKey, next.mimeType);
    if (prev?.url) prefetchMedia(prev.id, prev.url, albumKey, prev.mimeType);
  }, [index, items, albumKey]);

  // In presentation mode the carousel wraps around so the slideshow keeps
  // running indefinitely; outside of it we clamp to the bounds as before.
  const goPrev = useCallback(() => {
    setIndex((i) =>
      presentingRef.current ? (i - 1 + items.length) % items.length : i > 0 ? i - 1 : i,
    );
    showChrome();
  }, [items.length, showChrome]);
  const goNext = useCallback(() => {
    setIndex((i) =>
      presentingRef.current ? (i + 1) % items.length : i < items.length - 1 ? i + 1 : i,
    );
    showChrome();
  }, [items.length, showChrome]);

  const current = items[index];

  // The delete callback is single-shot per item. We close the viewer if the
  // album becomes empty, otherwise advance to the next (or stay, falling back
  // to the previous when we just removed the last one).
  async function handleDelete() {
    if (!onDelete || !current) return;
    if (!window.confirm(`Eliminare definitivamente "${current.filename}"?`)) return;
    setDeleting(true);
    try {
      await onDelete(current.id);
      if (items.length <= 1) {
        onClose();
        return;
      }
      setIndex((i) => Math.min(i, items.length - 2));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
    } finally {
      setDeleting(false);
    }
  }

  if (!current) return null;

  const overlay = (
    <div
      className="fixed inset-0 z-[100] flex select-none flex-col bg-[#02060f]/95 backdrop-blur-md"
      onMouseMove={handleMouseMove}
      onTouchStart={showChrome}
    >
      <Header
        visible={chromeVisible}
        title={current.filename}
        position={`${index + 1} / ${items.length}`}
        onClose={onClose}
        presenting={presenting}
        onTogglePresenting={() => setPresenting((p) => !p)}
        {...(onDelete ? { onDelete: handleDelete, deleting } : {})}
        rating={current.rating ?? null}
        {...(onRate ? { onRate: (n: number) => onRate(current.id, n) } : {})}
        {...(onEditTags ? { onEditTags: () => onEditTags(current.id) } : {})}
        {...(current.info ? { onToggleInfo: () => setInfoOpen((v) => !v), infoOpen } : {})}
      />
      {presenting ? (
        <Slideshow
          items={items}
          index={index}
          albumKey={albumKey}
          onSwipeLeft={goNext}
          onSwipeRight={goPrev}
          onMiddleTap={() => setChromeVisible((v) => !v)}
          onClose={onClose}
        />
      ) : (
        <Stage
          key={current.id}
          item={current}
          albumKey={albumKey}
          onSwipeLeft={goNext}
          onSwipeRight={goPrev}
          onTap={() => setChromeVisible((v) => !v)}
          onClose={onClose}
        />
      )}
      {items.length > 1 ? (
        <NavArrows
          visible={chromeVisible}
          canPrev={presenting || index > 0}
          canNext={presenting || index < items.length - 1}
          onPrev={goPrev}
          onNext={goNext}
        />
      ) : null}
      {items.length > 1 && !presenting ? (
        <ThumbnailStrip
          visible={chromeVisible}
          items={items}
          activeIndex={index}
          albumKey={albumKey}
          onPick={(i) => {
            setIndex(i);
            showChrome();
          }}
        />
      ) : null}
      {current.info ? (
        <InfoPanel
          open={infoOpen}
          item={current}
          albumKey={albumKey}
          onClose={() => setInfoOpen(false)}
        />
      ) : null}
    </div>
  );

  return createPortal(overlay, document.body);
}

function Header({
  visible,
  title,
  position,
  onClose,
  onDelete,
  deleting,
  presenting,
  onTogglePresenting,
  rating,
  onRate,
  onEditTags,
  onToggleInfo,
  infoOpen,
}: {
  visible: boolean;
  title: string;
  position: string;
  onClose: () => void;
  onDelete?: () => void;
  deleting?: boolean;
  presenting: boolean;
  onTogglePresenting: () => void;
  rating: number | null;
  onRate?: (n: number) => void | Promise<void>;
  onEditTags?: () => void;
  onToggleInfo?: () => void;
  infoOpen?: boolean;
}) {
  // Show stars only when there is a rating to display or when the user can
  // edit. Hides the affordance entirely on read-only viewer-mode for unrated
  // photos to avoid implying the viewer has anything to do here.
  const showStars = onRate !== undefined || (rating ?? 0) > 0;
  return (
    <div
      className={
        'pointer-events-none absolute inset-x-0 top-0 z-30 flex items-center justify-between gap-3 bg-gradient-to-b from-black/70 via-black/40 to-transparent px-4 pt-[env(safe-area-inset-top)] pb-12 transition-opacity duration-300 sm:px-6 ' +
        (visible ? 'opacity-100' : 'opacity-0')
      }
    >
      <div className="pointer-events-auto flex min-w-0 flex-1 flex-col text-white">
        <span className="truncate font-display text-sm font-semibold sm:text-base" title={title}>
          {title}
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
          {position}
        </span>
        {showStars ? (
          <StarRating
            value={rating}
            size="md"
            className="mt-1"
            {...(onRate ? { onChange: onRate } : {})}
          />
        ) : null}
      </div>
      <div className="pointer-events-auto flex items-center gap-2">
        {onToggleInfo ? (
          <button
            type="button"
            onClick={onToggleInfo}
            aria-label={infoOpen ? 'Nascondi info' : 'Mostra info'}
            aria-pressed={infoOpen ?? false}
            title={infoOpen ? 'Nascondi info' : 'Mostra info'}
            className={
              'flex h-11 w-11 items-center justify-center rounded-full backdrop-blur transition-colors ' +
              (infoOpen
                ? 'bg-cyan/30 text-white hover:bg-cyan/40'
                : 'bg-white/10 text-white hover:bg-white/20')
            }
          >
            <Icon name="info" className="text-xl" />
          </button>
        ) : null}
        {onEditTags ? (
          <button
            type="button"
            onClick={onEditTags}
            aria-label="Modifica tag"
            title="Modifica tag"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
          >
            <Icon name="sell" className="text-xl" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={onTogglePresenting}
          aria-label={presenting ? 'Esci dalla presentazione' : 'Avvia presentazione (F11)'}
          aria-pressed={presenting}
          title={presenting ? 'Esci dalla presentazione (Esc)' : 'Avvia presentazione (F11)'}
          className={
            'flex h-11 w-11 items-center justify-center rounded-full backdrop-blur transition-colors ' +
            (presenting
              ? 'bg-cyan/30 text-white hover:bg-cyan/40'
              : 'bg-white/10 text-white hover:bg-white/20')
          }
        >
          <Icon name={presenting ? 'pause' : 'slideshow'} className="text-xl" />
        </button>
        {onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Elimina"
            disabled={deleting}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-error/80 disabled:opacity-50"
          >
            <Icon name={deleting ? 'hourglass_empty' : 'delete'} className="text-xl" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label="Chiudi"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
        >
          <Icon name="close" className="text-xl" />
        </button>
      </div>
    </div>
  );
}

function NavArrows({
  visible,
  canPrev,
  canNext,
  onPrev,
  onNext,
}: {
  visible: boolean;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const baseBtn =
    'pointer-events-auto hidden h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-all hover:bg-white/20 disabled:opacity-30 sm:flex';
  return (
    <div
      className={
        'pointer-events-none absolute inset-y-0 left-0 right-0 z-20 flex items-center justify-between px-4 transition-opacity duration-300 sm:px-6 ' +
        (visible ? 'opacity-100' : 'opacity-0')
      }
    >
      <button
        type="button"
        aria-label="Precedente"
        disabled={!canPrev}
        onClick={onPrev}
        className={baseBtn}
      >
        <Icon name="chevron_left" className="text-2xl" />
      </button>
      <button
        type="button"
        aria-label="Successiva"
        disabled={!canNext}
        onClick={onNext}
        className={baseBtn}
      >
        <Icon name="chevron_right" className="text-2xl" />
      </button>
    </div>
  );
}

interface StageProps {
  item: MediaItem;
  albumKey: CryptoKey;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  onTap: () => void;
  onClose: () => void;
}

function Stage({ item, albumKey, onSwipeLeft, onSwipeRight, onTap, onClose }: StageProps) {
  const source = useMediaSource(
    item.id,
    item.url,
    albumKey,
    item.mimeType ? { mimeType: item.mimeType } : undefined,
  );
  const kind = mediaKindOf(item.filename);
  const { blurred } = useBlur();

  // Transform state for pinch / wheel zoom + pan.
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gesture = useRef<{
    mode: 'idle' | 'pan' | 'pinch';
    startScale: number;
    startDistance: number;
    startMid: { x: number; y: number };
    startTransform: { x: number; y: number };
    startPointer: { x: number; y: number };
    pointerStartTime: number;
    moved: boolean;
    lastTapAt: number;
  }>({
    mode: 'idle',
    startScale: 1,
    startDistance: 0,
    startMid: { x: 0, y: 0 },
    startTransform: { x: 0, y: 0 },
    startPointer: { x: 0, y: 0 },
    pointerStartTime: 0,
    moved: false,
    lastTapAt: 0,
  });

  const reset = useCallback(() => {
    setTransform({ scale: 1, x: 0, y: 0 });
  }, []);

  // Reset zoom when item changes.
  useEffect(() => {
    reset();
  }, [item.id, reset]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesture.current.moved = false;
    gesture.current.pointerStartTime = performance.now();
    if (pointers.current.size === 2) {
      const pair = [...pointers.current.values()];
      const a = pair[0]!;
      const b = pair[1]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      gesture.current.mode = 'pinch';
      gesture.current.startScale = transformRef.current.scale;
      gesture.current.startDistance = Math.hypot(dx, dy);
      gesture.current.startMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      gesture.current.startTransform = { x: transformRef.current.x, y: transformRef.current.y };
    } else {
      gesture.current.mode = 'pan';
      gesture.current.startPointer = { x: e.clientX, y: e.clientY };
      gesture.current.startTransform = { x: transformRef.current.x, y: transformRef.current.y };
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const stored = pointers.current.get(e.pointerId);
    if (!stored) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (gesture.current.mode === 'pinch' && pointers.current.size === 2) {
      const pair = [...pointers.current.values()];
      const a = pair[0]!;
      const b = pair[1]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / Math.max(1, gesture.current.startDistance);
      const nextScale = clamp(gesture.current.startScale * ratio, MIN_SCALE, MAX_SCALE);
      gesture.current.moved = true;
      setTransform((prev) => ({ ...prev, scale: nextScale }));
      return;
    }

    if (gesture.current.mode === 'pan') {
      const dx = e.clientX - gesture.current.startPointer.x;
      const dy = e.clientY - gesture.current.startPointer.y;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) gesture.current.moved = true;
      if (transformRef.current.scale > 1.01) {
        // Pan when zoomed in.
        setTransform((prev) => ({
          ...prev,
          x: gesture.current.startTransform.x + dx,
          y: gesture.current.startTransform.y + dy,
        }));
      }
    }
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const stored = pointers.current.get(e.pointerId);
    pointers.current.delete(e.pointerId);

    const wasPinch = gesture.current.mode === 'pinch';
    const moved = gesture.current.moved;
    const elapsed = performance.now() - gesture.current.pointerStartTime;

    if (pointers.current.size === 0) {
      gesture.current.mode = 'idle';

      // Tap / double-tap detection. Stories-style edge taps: a single tap on
      // the outer EDGE_TAP_RATIO of the viewport navigates (left lembo →
      // previous, right lembo → next), middle stripe toggles chrome.
      if (!moved && elapsed < 250 && stored && transformRef.current.scale <= 1.01) {
        const now = performance.now();
        if (now - gesture.current.lastTapAt < DOUBLE_TAP_MS) {
          // Double tap: zoom in to 2.5x toward tap point.
          setTransform({ scale: 2.5, x: 0, y: 0 });
          gesture.current.lastTapAt = 0;
        } else {
          gesture.current.lastTapAt = now;
          const tapX = e.clientX;
          // Single tap fires after short delay if not promoted to double tap.
          setTimeout(() => {
            if (gesture.current.lastTapAt === now) {
              const w = window.innerWidth;
              if (tapX < w * EDGE_TAP_RATIO) onSwipeRight();
              else if (tapX > w * (1 - EDGE_TAP_RATIO)) onSwipeLeft();
              else onTap();
            }
          }, DOUBLE_TAP_MS);
        }
        return;
      }

      // Double-tap to reset when zoomed.
      if (!moved && elapsed < 250 && transformRef.current.scale > 1.01) {
        const now = performance.now();
        if (now - gesture.current.lastTapAt < DOUBLE_TAP_MS) {
          reset();
          gesture.current.lastTapAt = 0;
          return;
        }
        gesture.current.lastTapAt = now;
      }

      // Horizontal swipe → nav (only when not zoomed).
      if (
        !wasPinch &&
        stored &&
        transformRef.current.scale <= 1.01 &&
        Math.abs(e.clientX - gesture.current.startPointer.x) > SWIPE_THRESHOLD_PX
      ) {
        if (e.clientX < gesture.current.startPointer.x) onSwipeLeft();
        else onSwipeRight();
        return;
      }

      // Vertical swipe down (when not zoomed) → close.
      if (
        !wasPinch &&
        stored &&
        transformRef.current.scale <= 1.01 &&
        e.clientY - gesture.current.startPointer.y > 100 &&
        Math.abs(e.clientY - gesture.current.startPointer.y) >
          Math.abs(e.clientX - gesture.current.startPointer.x)
      ) {
        onClose();
      }
    } else if (pointers.current.size === 1) {
      // Lifted one finger from a pinch — switch back to pan.
      const remaining = [...pointers.current.values()][0]!;
      gesture.current.mode = 'pan';
      gesture.current.startPointer = remaining;
      gesture.current.startTransform = { x: transformRef.current.x, y: transformRef.current.y };
    }
  };

  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0025;
    const nextScale = clamp(transformRef.current.scale * (1 + delta), MIN_SCALE, MAX_SCALE);
    setTransform((prev) =>
      nextScale === 1 ? { scale: 1, x: 0, y: 0 } : { ...prev, scale: nextScale },
    );
  };

  const stylesTransform = `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`;

  return (
    <div
      className="relative flex flex-1 items-center justify-center overflow-hidden"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      style={{ touchAction: 'none' }}
    >
      {source.state === 'loading' ? (
        <div className="flex flex-col items-center gap-3 text-white/70">
          <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-white/40 border-r-transparent" />
          <p className="text-xs uppercase tracking-[0.2em]">
            {source.phase ? VIEWER_PHASE_LABEL[source.phase] : 'Decifro…'}
          </p>
        </div>
      ) : source.state === 'error' ? (
        <div className="flex flex-col items-center gap-2 text-white/80">
          <Icon name="error" className="text-3xl" />
          <p className="text-sm">Errore: {source.error}</p>
        </div>
      ) : kind === 'video' ? (
        <video
          src={source.blobUrl}
          controls
          autoPlay
          playsInline
          className={cn('max-h-full max-w-full', blurred && 'scale-110 blur-3xl')}
          style={{
            transform: stylesTransform,
            transition: pointers.current.size === 0 ? 'transform 200ms ease-out' : 'none',
            willChange: 'transform',
          }}
        />
      ) : (
        <img
          src={source.blobUrl}
          alt={item.filename}
          draggable={false}
          className={cn('max-h-full max-w-full object-contain', blurred && 'scale-110 blur-3xl')}
          style={{
            transform: stylesTransform,
            transition: pointers.current.size === 0 ? 'transform 200ms ease-out' : 'none',
            willChange: 'transform',
          }}
        />
      )}
    </div>
  );
}

function ThumbnailStrip({
  visible,
  items,
  activeIndex,
  albumKey,
  onPick,
}: {
  visible: boolean;
  items: MediaItem[];
  activeIndex: number;
  albumKey: CryptoKey;
  onPick: (i: number) => void;
}) {
  const activeRef = useRef<HTMLButtonElement>(null);

  // Center the active thumb on change.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activeIndex]);

  return (
    <div
      className={
        'absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/80 via-black/50 to-transparent px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-10 transition-opacity duration-300 ' +
        (visible ? 'opacity-100' : 'pointer-events-none opacity-0')
      }
    >
      <div className="flex w-full gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((it, i) => (
          <ThumbButton
            key={it.id}
            ref={i === activeIndex ? activeRef : null}
            item={it}
            albumKey={albumKey}
            active={i === activeIndex}
            onClick={() => onPick(i)}
          />
        ))}
      </div>
    </div>
  );
}

interface ThumbButtonProps {
  item: MediaItem;
  albumKey: CryptoKey;
  active: boolean;
  onClick: () => void;
}

const ThumbButton = forwardRef<HTMLButtonElement, ThumbButtonProps>(function ThumbButton(
  { item, albumKey, active, onClick },
  ref,
) {
  // Decrypt lazily: when the thumb enters the viewport (or it's active), we
  // request the full-resolution blob. Active thumbs are decrypted unconditionally
  // because the stage already paid for that decrypt — same cache entry.
  const localRef = useRef<HTMLButtonElement | null>(null);
  const setRefs = useCallback(
    (node: HTMLButtonElement | null) => {
      localRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    },
    [ref],
  );

  const [shouldLoad, setShouldLoad] = useState(active);

  useEffect(() => {
    if (active) {
      setShouldLoad(true);
      return;
    }
    const el = localRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
            break;
          }
        }
      },
      { root: el.parentElement, rootMargin: '200px 200px', threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [active]);

  const source = useMediaSource(
    shouldLoad ? item.id : null,
    shouldLoad ? item.url : null,
    shouldLoad ? albumKey : null,
    { thumb: true, ...(item.mimeType ? { mimeType: item.mimeType } : {}) },
  );
  const kind = mediaKindOf(item.filename);

  return (
    <button
      ref={setRefs}
      type="button"
      onClick={onClick}
      aria-label={item.filename}
      aria-current={active ? 'true' : undefined}
      className={
        'relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-black/40 transition-all sm:h-20 sm:w-20 ' +
        (active
          ? 'border-cyan shadow-[0_0_0_3px_rgba(100,255,218,0.25)]'
          : 'border-white/10 opacity-60 hover:opacity-100')
      }
    >
      {source.state === 'ready' ? (
        <img src={source.blobUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-white/40">
          <Icon
            name={
              kind === 'video'
                ? 'play_circle'
                : source.state === 'loading'
                  ? 'image'
                  : 'broken_image'
            }
            className="text-lg"
          />
        </span>
      )}
      {kind === 'video' && source.state === 'ready' ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
          <Icon name="play_circle" className="text-2xl" filled />
        </span>
      ) : null}
    </button>
  );
});

interface SlideshowProps {
  items: MediaItem[];
  index: number;
  albumKey: CryptoKey;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  onMiddleTap: () => void;
  onClose: () => void;
}

interface SlideLayerData {
  item: MediaItem;
  variant: number;
  key: number;
}

// Apple/Google-Photos-style slideshow. Two fixed layers (A/B) crossfade into
// each other on every index change; the active layer holds the current item
// while the inactive one keeps the outgoing image painted long enough to fade
// out smoothly. Each layer plays one of four Ken Burns variants picked at
// random so consecutive slides don't share the same motion.
function Slideshow({
  items,
  index,
  albumKey,
  onSwipeLeft,
  onSwipeRight,
  onMiddleTap,
  onClose,
}: SlideshowProps) {
  const current = items[index] ?? null;
  const keyRef = useRef(0);
  const [slotA, setSlotA] = useState<SlideLayerData | null>(() =>
    current ? { item: current, variant: pickKenBurnsVariant(), key: ++keyRef.current } : null,
  );
  const [slotB, setSlotB] = useState<SlideLayerData | null>(null);
  const [active, setActive] = useState<'a' | 'b'>('a');

  useEffect(() => {
    if (!current) return;
    const activeSlot = active === 'a' ? slotA : slotB;
    if (activeSlot?.item.id === current.id) return;
    const fresh: SlideLayerData = {
      item: current,
      variant: pickKenBurnsVariant(),
      key: ++keyRef.current,
    };
    if (active === 'a') {
      setSlotB(fresh);
      setActive('b');
    } else {
      setSlotA(fresh);
      setActive('a');
    }
  }, [current?.id]);

  const gesture = useRef({
    startX: 0,
    startY: 0,
    startTime: 0,
    pointerId: -1,
    moved: false,
  });

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    gesture.current.startX = e.clientX;
    gesture.current.startY = e.clientY;
    gesture.current.startTime = performance.now();
    gesture.current.pointerId = e.pointerId;
    gesture.current.moved = false;
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== gesture.current.pointerId) return;
    const dx = e.clientX - gesture.current.startX;
    const dy = e.clientY - gesture.current.startY;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) gesture.current.moved = true;
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== gesture.current.pointerId) return;
    gesture.current.pointerId = -1;
    const dx = e.clientX - gesture.current.startX;
    const dy = e.clientY - gesture.current.startY;
    const elapsed = performance.now() - gesture.current.startTime;

    if (!gesture.current.moved && elapsed < 250) {
      const w = window.innerWidth;
      if (e.clientX < w * EDGE_TAP_RATIO) onSwipeRight();
      else if (e.clientX > w * (1 - EDGE_TAP_RATIO)) onSwipeLeft();
      else onMiddleTap();
      return;
    }

    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) onSwipeLeft();
      else onSwipeRight();
      return;
    }
    if (dy > 100 && Math.abs(dy) > Math.abs(dx)) onClose();
  };

  return (
    <div
      className="relative flex flex-1 items-center justify-center overflow-hidden bg-black"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ touchAction: 'none' }}
    >
      <SlideLayer slot={slotA} albumKey={albumKey} visible={active === 'a'} />
      <SlideLayer slot={slotB} albumKey={albumKey} visible={active === 'b'} />
    </div>
  );
}

function SlideLayer({
  slot,
  albumKey,
  visible,
}: {
  slot: SlideLayerData | null;
  albumKey: CryptoKey;
  visible: boolean;
}) {
  const source = useMediaSource(
    slot?.item.id ?? null,
    slot?.item.url ?? null,
    slot ? albumKey : null,
    slot?.item.mimeType ? { mimeType: slot.item.mimeType } : undefined,
  );
  if (!slot) return null;
  const kind = mediaKindOf(slot.item.filename);
  const ready = source.state === 'ready';
  const animationStyle = {
    animationName: `pv-kenburns-${slot.variant}`,
    animationDuration: `${KENBURNS_DURATION_MS}ms`,
    animationTimingFunction: 'ease-out',
    animationFillMode: 'forwards',
    willChange: 'transform',
  } as const;
  return (
    <div
      key={slot.key}
      className="absolute inset-0 flex items-center justify-center"
      style={{
        opacity: visible && ready ? 1 : 0,
        transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
      }}
    >
      {ready && kind === 'image' ? (
        <img
          src={source.blobUrl}
          alt={slot.item.filename}
          draggable={false}
          className="h-full w-full object-contain"
          style={animationStyle}
        />
      ) : ready && kind === 'video' ? (
        <video
          src={source.blobUrl}
          autoPlay
          muted
          playsInline
          loop
          className="h-full w-full object-contain"
          style={animationStyle}
        />
      ) : null}
    </div>
  );
}

function pickKenBurnsVariant(): number {
  return 1 + Math.floor(Math.random() * KENBURNS_VARIANTS);
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

// Right-side drawer that decrypts `encryptedMeta` lazily and renders every
// available metadata field. Works on legacy v1 photos (filename/mimeType/size
// only) and on v2 photos with the EXIF block (capturedAt/GPS/device/lens/
// exposure/orientation/dimensions). Plaintext Photo row fields (uploadedAt,
// encrypted blob size, keyGen) are always shown.
function InfoPanel({
  open,
  item,
  albumKey,
  onClose,
}: {
  open: boolean;
  item: MediaItem;
  albumKey: CryptoKey;
  onClose: () => void;
}) {
  const info = item.info!;
  const [meta, setMeta] = useState<cv.PhotoMeta | null | 'loading' | 'error'>('loading');

  // Re-decrypt only when the panel is open AND the item changes — avoids
  // paying the cost for users that never tap Info, and lets the user navigate
  // through the carousel with the panel pinned.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setMeta('loading');
    cv.decryptText(info.encryptedMeta, albumKey)
      .then((json) => {
        if (cancelled) return;
        const parsed = cv.parsePhotoMeta(json);
        setMeta(parsed ?? 'error');
      })
      .catch(() => {
        if (!cancelled) setMeta('error');
      });
    return () => {
      cancelled = true;
    };
  }, [open, info.encryptedMeta, albumKey]);

  // Build the rows once meta lands. Order: identity, dates, capture optics,
  // device, location, storage internals. Sezioni con zero righe sono nascoste.
  const sections = useMemo(() => buildInfoSections(meta, info), [meta, info]);

  return (
    <>
      {/* Tap-to-dismiss scrim. Sits below the chrome so the close X stays clickable. */}
      <button
        type="button"
        aria-label="Chiudi info"
        onClick={onClose}
        className={cn(
          'absolute inset-0 z-30 cursor-default bg-black/30 transition-opacity duration-200 sm:bg-transparent',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        tabIndex={-1}
      />
      <aside
        aria-label="Informazioni foto"
        aria-hidden={!open}
        className={cn(
          'absolute inset-y-0 right-0 z-40 flex w-full max-w-md flex-col overflow-hidden bg-[#0a1224]/95 text-white backdrop-blur-xl transition-transform duration-300 ease-out',
          'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
          'shadow-[-12px_0_32px_rgba(0,0,0,0.45)]',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-2">
            <Icon name="info" className="text-lg text-cyan" />
            <h3 className="font-display text-base font-semibold">Informazioni</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <Icon name="close" className="text-lg" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {meta === 'loading' ? (
            <p className="text-sm text-white/60">Decifro metadati…</p>
          ) : meta === 'error' ? (
            <p className="text-sm text-error">Impossibile leggere i metadati cifrati.</p>
          ) : sections.length === 0 ? (
            <p className="text-sm text-white/60">Nessun metadato disponibile.</p>
          ) : (
            <dl className="flex flex-col gap-5">
              {sections.map((section) => (
                <div key={section.title}>
                  <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan/80">
                    {section.title}
                  </h4>
                  <div className="flex flex-col gap-1.5">
                    {section.rows.map((row) => (
                      <div
                        key={row.label}
                        className="flex items-baseline justify-between gap-3 border-b border-white/5 pb-1.5 last:border-b-0 last:pb-0"
                      >
                        <dt className="shrink-0 text-xs text-white/50">{row.label}</dt>
                        <dd
                          className="break-words text-right text-sm text-white"
                          title={row.title ?? row.value}
                        >
                          {row.value}
                        </dd>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </dl>
          )}
        </div>
      </aside>
    </>
  );
}

interface InfoRow {
  label: string;
  value: string;
  /** Optional tooltip when the rendered value is truncated/abbreviated. */
  title?: string;
}
interface InfoSection {
  title: string;
  rows: InfoRow[];
}

function buildInfoSections(
  meta: cv.PhotoMeta | null | 'loading' | 'error',
  info: MediaItemInfo,
): InfoSection[] {
  const sections: InfoSection[] = [];
  const m = meta && meta !== 'loading' && meta !== 'error' ? meta : null;

  // Identità del file. Per v1 mostriamo solo filename/mime/size — non c'è
  // niente di più nel payload cifrato. Per v2 si aggiunge poi la sezione EXIF.
  const identity: InfoRow[] = [];
  if (m?.filename) identity.push({ label: 'Nome', value: m.filename });
  if (m?.mimeType) identity.push({ label: 'Tipo MIME', value: m.mimeType });
  if (m && typeof m.size === 'number' && m.size > 0) {
    identity.push({ label: 'Dimensione originale', value: formatBytes(m.size) });
  }
  if (identity.length > 0) sections.push({ title: 'File', rows: identity });

  // Date. uploadedAt è plaintext (createdAt della riga DDB). capturedAt è
  // dentro il blob v2 (assente per blob v1 / video / file senza EXIF).
  const dates: InfoRow[] = [];
  if (m?.v === 2 && m.exif?.capturedAt) {
    dates.push({
      label: 'Data scatto',
      value: formatDateTime(m.exif.capturedAt),
      title: m.exif.capturedAt,
    });
  }
  dates.push({
    label: 'Caricata il',
    value: formatDateTime(info.uploadedAt),
    title: info.uploadedAt,
  });
  sections.push({ title: 'Date', rows: dates });

  // EXIF — solo per blob v2 con il blocco popolato.
  if (m?.v === 2 && m.exif) {
    const e = m.exif;

    const capture: InfoRow[] = [];
    if (e.exposure?.iso !== undefined)
      capture.push({ label: 'ISO', value: String(e.exposure.iso) });
    if (e.exposure?.fNumber !== undefined) {
      capture.push({ label: 'Apertura', value: `f/${e.exposure.fNumber}` });
    }
    if (e.exposure?.shutterSpeed) {
      capture.push({ label: 'Otturatore', value: e.exposure.shutterSpeed });
    }
    if (e.exposure?.focalLength !== undefined) {
      capture.push({ label: 'Focale', value: `${e.exposure.focalLength} mm` });
    }
    if (e.exposure?.focalLength35mm !== undefined) {
      capture.push({
        label: 'Focale (eq. 35mm)',
        value: `${e.exposure.focalLength35mm} mm`,
      });
    }
    if (capture.length > 0) sections.push({ title: 'Esposizione', rows: capture });

    const optics: InfoRow[] = [];
    if (e.lens?.make) optics.push({ label: 'Produttore obiettivo', value: e.lens.make });
    if (e.lens?.model) optics.push({ label: 'Obiettivo', value: e.lens.model });
    if (optics.length > 0) sections.push({ title: 'Obiettivo', rows: optics });

    const device: InfoRow[] = [];
    if (e.device?.make) device.push({ label: 'Produttore', value: e.device.make });
    if (e.device?.model) device.push({ label: 'Modello', value: e.device.model });
    if (e.device?.software) device.push({ label: 'Software', value: e.device.software });
    if (device.length > 0) sections.push({ title: 'Dispositivo', rows: device });

    const location: InfoRow[] = [];
    if (e.gps) {
      location.push({
        label: 'Coordinate',
        value: `${e.gps.lat.toFixed(6)}, ${e.gps.lng.toFixed(6)}`,
      });
      if (e.gps.alt !== undefined) {
        location.push({ label: 'Altitudine', value: `${e.gps.alt.toFixed(1)} m` });
      }
    }
    if (location.length > 0) sections.push({ title: 'Posizione', rows: location });

    const dimensions: InfoRow[] = [];
    if (e.pixelWidth !== undefined && e.pixelHeight !== undefined) {
      dimensions.push({
        label: 'Risoluzione',
        value: `${e.pixelWidth} × ${e.pixelHeight} px`,
      });
    }
    if (e.orientation !== undefined) {
      dimensions.push({ label: 'Orientamento EXIF', value: String(e.orientation) });
    }
    if (dimensions.length > 0) sections.push({ title: 'Immagine', rows: dimensions });
  }

  // Internals: utili per il debug + per dare contesto su rotation/keygen.
  // Volutamente in fondo.
  const internals: InfoRow[] = [
    { label: 'Schema meta', value: m ? `v${m.v ?? 1}` : 'sconosciuto' },
    { label: 'Blob cifrato', value: formatBytes(info.encryptedSize) },
    { label: 'Generazione chiave', value: String(info.keyGen ?? 1) },
  ];
  sections.push({ title: 'Storage', rows: internals });

  return sections;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('it-IT', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
